import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Loader2,
  Copy,
  BookMarked,
  MessageCircle,
  Sparkles
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const books = [
  { value: 'genesis', label: 'Génesis' },
  { value: 'exodo', label: 'Éxodo' },
  { value: 'mateo', label: 'Mateo' },
  { value: 'juan', label: 'Juan' },
  { value: '1nefi', label: '1 Nefi' },
  { value: '2nefi', label: '2 Nefi' },
  { value: 'alma', label: 'Alma' },
  { value: '3nefi', label: '3 Nefi' },
  { value: 'moroni', label: 'Moroni' },
  { value: 'dyc', label: 'Doctrina y Convenios' },
  { value: 'moises', label: 'Moisés' },
  { value: 'abraham', label: 'Abraham' },
];

const SeminaryMode = () => {
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    book: '',
    chapter: '',
    verses: '',
    verseText: '',
    topic: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.book || !formData.chapter || !formData.verses || !formData.verseText) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/seminary-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          book: books.find(b => b.value === formData.book)?.label || formData.book,
          chapter: parseInt(formData.chapter),
          verses: formData.verses,
          verse_text: formData.verseText,
          topic: formData.topic || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.response);
        toast.success('Material generado exitosamente');
      } else {
        toast.error('Error al generar el material');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      toast.success('Copiado al portapapeles');
    }
  };

  const extractWhatsAppMessage = () => {
    if (!result) return '';
    
    const whatsappMatch = result.match(/mensaje para whatsapp[:\s]*(.{1,300})/i);
    if (whatsappMatch) {
      return whatsappMatch[1].trim();
    }
    return result.slice(0, 280);
  };

  const shareWhatsApp = () => {
    const message = extractWhatsAppMessage();
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const saveAsNote = async () => {
    if (!noteTitle.trim()) {
      toast.error('Ingresa un título para la nota');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: noteTitle,
          content: result,
          scripture_ref: `${books.find(b => b.value === formData.book)?.label} ${formData.chapter}:${formData.verses}`,
          tags: ['seminario', 'clase', formData.book]
        })
      });

      if (response.ok) {
        toast.success('Material guardado exitosamente');
        setSaveDialogOpen(false);
        setNoteTitle('');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="seminary-mode">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <GraduationCap className="h-8 w-8 text-primary" />
            Modo Maestro de Seminario
          </h1>
          <p className="text-muted-foreground">
            Genera material didáctico completo para tus clases de seminario
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Configurar Clase</CardTitle>
              <CardDescription>
                Ingresa los detalles del pasaje a enseñar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Libro *</Label>
                    <Select
                      value={formData.book}
                      onValueChange={(value) => setFormData({ ...formData, book: value })}
                    >
                      <SelectTrigger data-testid="book-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {books.map((book) => (
                          <SelectItem key={book.value} value={book.value}>
                            {book.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Capítulo *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.chapter}
                      onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                      placeholder="Ej: 3"
                      data-testid="chapter-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Versículos *</Label>
                  <Input
                    value={formData.verses}
                    onChange={(e) => setFormData({ ...formData, verses: e.target.value })}
                    placeholder="Ej: 1-5 o 1,3,5-7"
                    data-testid="verses-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Texto de los versículos *</Label>
                  <Textarea
                    value={formData.verseText}
                    onChange={(e) => setFormData({ ...formData, verseText: e.target.value })}
                    placeholder="Escribe o pega el texto de los versículos aquí..."
                    className="min-h-[150px]"
                    data-testid="verse-text-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tema específico (opcional)</Label>
                  <Input
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    placeholder="Ej: La fe, El arrepentimiento..."
                    data-testid="topic-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generando material...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generar Material de Clase
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Material Generado</CardTitle>
              <CardDescription>
                Tu material de clase aparecerá aquí
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar Todo
                    </Button>
                    <Button variant="outline" size="sm" onClick={shareWhatsApp}>
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Enviar WhatsApp
                    </Button>
                    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <BookMarked className="h-4 w-4 mr-1" />
                          Guardar
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Guardar Material</DialogTitle>
                          <DialogDescription>
                            Guarda este material en tu repositorio personal
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Título</Label>
                            <Input
                              value={noteTitle}
                              onChange={(e) => setNoteTitle(e.target.value)}
                              placeholder="Ej: Clase de Génesis 3"
                              data-testid="save-title-input"
                            />
                          </div>
                          <Button
                            onClick={saveAsNote}
                            disabled={saving}
                            className="w-full"
                            data-testid="confirm-save-btn"
                          >
                            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Guardar Material
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <ScrollArea className="h-[450px] pr-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none markdown-content">
                      {result.split('\n').map((paragraph, i) => (
                        <p key={`seminary-p-${i}`}>{paragraph}</p>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[450px] text-muted-foreground">
                  <GraduationCap className="h-16 w-16 mb-4 opacity-20" />
                  <p className="text-center">
                    Completa el formulario y genera tu material de clase
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default SeminaryMode;
